import React, { useEffect, useRef, useState } from "react";
import instance from "../../Interceptor/api_instance";
import { useTranslation } from "react-i18next";

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const DriverSearch = ({ setState, state }) => {
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const cacheRef = useRef(new Map()); // query -> { data, ts }
  const lastQueryRef = useRef(""); // guards against stale responses
  const { t } = useTranslation();

  const normalizeQuery = () =>
    `${state.firstName || ""} ${state.lastName || ""}`
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const handleClick = (option) => {
    setState((prev) => ({
      ...prev,
      firstName: option.first_name,
      lastName: option.last_name,
      isActive: option.is_active
    }));
    setIsClicked(true);

    setFilteredDrivers([]);
  };

  const fetchOptions = async () => {
    const query = normalizeQuery();
    if (!query) {
      setFilteredDrivers([]);
      return;
    }

    const now = Date.now();
    const cached = cacheRef.current.get(query);
    if (cached && now - cached.ts < CACHE_TTL) {
      setFilteredDrivers(cached.data);
      return;
    }

    setIsLoading(true);
    lastQueryRef.current = query;
    try {
      const res = await instance.get("safety/get-all-drivers", {
        params: { name: query },
      });
      const data = res.data || [];
      // Only set state if this response matches the latest query
      if (lastQueryRef.current === query) {
        setFilteredDrivers(data);
        cacheRef.current.set(query, { data, ts: now });
      }
    } catch (err) {
      console.error("Failed to fetch drivers:", err);
      if (lastQueryRef.current === query) setFilteredDrivers([]);
    } finally {
      if (lastQueryRef.current === query) setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if ((state.firstName || state.lastName) && !isClicked) fetchOptions();
      else {
        setFilteredDrivers([]);
        setIsClicked(false);
      }
    }, 500); // debounce
    return () => clearTimeout(handler);
  }, [state.firstName, state.lastName]);

  return (
    <>
      <div className="input-cont">
        <input
          type="text"
          id="firstName"
          data-testid="firstName"
          name="firstName"
          className="text-input"
          placeholder={t("forms.type.newDriver.input-headers.firstName")}
          onChange={(e) => {
            handleChange(e);
            setIsClicked(false);
          }}
          value={state.firstName}
          autoComplete="off"
          required
        />
        <input
          type="text"
          id="lastName"
          data-testid="lastName"
          name="lastName"
          className="text-input"
          placeholder={t("forms.type.newDriver.input-headers.lastName")}
          onChange={(e) => {
            handleChange(e);
            setIsClicked(false);
          }}
          value={state.lastName}
          autoComplete="off"
          required
        />
        <div>
        {state.isActive !== undefined && (
          <div className={` ${state.isActive ? "success" : "error"}`}>
            {state.isActive ? t("driversTable.tabs.active") : t("driversTable.tabs.deactivated")}
          </div>
        )}
        </div>
      </div>

      {isLoading && <div style={{ fontSize: 12, opacity: 0.7 }}>{t("general.loading")}…</div>}

      {filteredDrivers.length > 0 && (
        <ul className="search-results">
          {filteredDrivers.map((driver, index) => (
            <li
              key={driver.id ?? index}
              onClick={() => handleClick(driver)}
              data-testid={`driver-option-${index}`}
              style={{
                padding: "8px",
                cursor: "pointer",
                borderBottom: "1px solid #ccc",
              }}
            >
              {driver.first_name} {driver.last_name}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default DriverSearch;
